import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { FileIcon } from "@/components/common/FileIcon";
import { HighlightedText } from "@/components/common/HighlightedText";
import { SearchInput } from "@/components/common/SearchInput";
import { ShortcutHint } from "@/components/common/ShortcutHint";
import {
	lookupEnvironmentFilesByIds,
	searchEnvironmentFiles,
	type EnvironmentFileDto,
} from "@/lib/api";
import { getFileIconType } from "@/lib/file-type";
import { buildQuickOpenItems, QuickOpenSearch, type QuickOpenItem } from "@/lib/quick-open";
import { OPEN_QUICK_OPEN_EVENT, type QuickOpenEventDetail } from "@/lib/quick-open-event";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

/**
 * Cmd/Ctrl+P で開く、環境ファイルのクイックオープンパレット。
 * AppLayout に一度だけマウントし、開閉状態はこのコンポーネント内に閉じ込める。
 */
export function QuickOpenDialog() {
	const [initialQuery, setInitialQuery] = useState<string | null>(null);
	const chatId = useChatStore((s) => s.chatId);

	// チャットを切り替えたら古い環境のファイルを開けないよう閉じる。
	useEffect(() => {
		setInitialQuery(null);
	}, [chatId]);

	useEffect(() => {
		const handler = (e: Event) => {
			setInitialQuery((e as CustomEvent<QuickOpenEventDetail>).detail?.query ?? "");
		};
		window.addEventListener(OPEN_QUICK_OPEN_EVENT, handler);
		return () => window.removeEventListener(OPEN_QUICK_OPEN_EVENT, handler);
	}, []);

	if (initialQuery === null) return null;
	return <QuickOpenPalette initialQuery={initialQuery} onClose={() => setInitialQuery(null)} />;
}

function QuickOpenPalette({
	initialQuery,
	onClose,
}: {
	initialQuery: string;
	onClose: () => void;
}) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const [query, setQuery] = useState(initialQuery);
	const [selectedIndex, setSelectedIndex] = useState(0);

	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const openFileIds = useChatStore((s) => s.openFileIds);
	const activeFileId = useChatStore((s) => s.activeFileId);
	const [candidates, setCandidates] = useState<EnvironmentFileDto[]>([]);
	const [searching, setSearching] = useState(environmentId !== null);
	const searchRef = useRef<QuickOpenSearch | null>(null);

	// 候補はサーバー検索で絞り込み、採点・並べ替えはその部分集合に対して行う。
	// 取得待ちの間も入力中のクエリで採点するため、絞り込みだけは即座に反映される。
	const items = useMemo(
		() => buildQuickOpenItems(candidates, query, openFileIds, activeFileId),
		[candidates, query, openFileIds, activeFileId],
	);
	const clampedIndex = items.length === 0 ? 0 : Math.min(selectedIndex, items.length - 1);

	useEffect(() => {
		if (environmentId === null) return;
		const search = new QuickOpenSearch({
			fetchCandidates: (q) =>
				q.trim() === ""
					? // 空クエリでは検索せず、開いているタブだけを候補にする。
						lookupEnvironmentFilesByIds(environmentId, useChatStore.getState().openFileIds)
					: searchEnvironmentFiles(environmentId, q),
			onCandidates: (files) => {
				setCandidates(files);
				setSearching(false);
			},
			onError: (error) => {
				setSearching(false);
				console.error("quick open search failed", error);
				useToastStore
					.getState()
					.show({ type: "error", message: "ファイルを検索できませんでした。" });
			},
		});
		searchRef.current = search;
		return () => {
			search.dispose();
			searchRef.current = null;
		};
	}, [environmentId]);

	useEffect(() => {
		if (environmentId === null) return;
		setSearching(true);
		searchRef.current?.setQuery(query);
	}, [environmentId, query]);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		el.showModal();
		inputRef.current?.select();
		return () => {
			if (el.open) el.close();
		};
	}, []);

	useEffect(() => {
		setSelectedIndex(0);
	}, [query]);

	useEffect(() => {
		listRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: "nearest" });
	}, [clampedIndex]);

	const handleOpenItem = useCallback(
		(item: QuickOpenItem) => {
			const store = useChatStore.getState();
			// ツリーを読み込まずに開くため、タブ表示に必要な DTO を検索結果から引き継ぐ。
			const file = candidates.find((candidate) => candidate.id === item.fileId);
			if (file) store.mergeOpenEnvironmentFileEntries([file]);
			store.selectEnvironmentFilesTreeNode(item.path);
			store.openFile(item.fileId);
			onClose();
		},
		[candidates, onClose],
	);

	const handleKeyDown = useCallback(
		(e: ReactKeyboardEvent<HTMLDialogElement>) => {
			if (e.nativeEvent.isComposing) return;
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "p") {
				// 開いたまま入力を再選択する (VS Code 同様、再度 Cmd+P しても閉じない)。
				e.preventDefault();
				inputRef.current?.select();
				return;
			}
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
				return;
			}
			if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((i) => Math.max(i - 1, 0));
				return;
			}
			if (e.key === "Enter") {
				e.preventDefault();
				const item = items[clampedIndex];
				if (item) handleOpenItem(item);
				return;
			}
			if (e.key === "Tab") {
				e.preventDefault();
			}
		},
		[items, clampedIndex, handleOpenItem],
	);

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			onCancel={(e) => {
				e.preventDefault();
				onClose();
			}}
			onClick={(e) => {
				// バックドロップ (= dialog 要素自身) クリックで閉じる「ライトディスミス」。
				if (e.target === e.currentTarget) onClose();
			}}
			className="fixed z-50 rounded-4 bg-surface-1 p-0 backdrop:bg-scrim"
			style={{
				top: 0,
				left: 0,
				right: 0,
				bottom: "auto",
				margin: "10vh auto 0",
				width: 560,
				maxWidth: "90vw",
				boxShadow: "var(--shadow-popover)",
			}}
			data-testid="quick-open-dialog"
		>
			<SearchInput
				ref={inputRef}
				variant="dialog"
				value={query}
				onChange={setQuery}
				placeholder="ファイル名を検索"
				testId="quick-open-input"
				trailing={<ShortcutHint combo="⌘P" />}
			/>
			<ul
				ref={listRef}
				data-testid="quick-open-list"
				style={{ margin: 0, padding: 4, listStyle: "none", maxHeight: 320, overflowY: "auto" }}
			>
				{items.length === 0 && (
					<li
						data-testid="quick-open-empty"
						style={{ padding: 12, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}
					>
						{searching
							? "検索中…"
							: query.trim() === ""
								? "ファイル名を入力して検索します"
								: "該当するファイルがありません"}
					</li>
				)}
				{items.map((item, i) => (
					<li
						key={item.fileId}
						data-testid={`quick-open-item-${item.path}`}
						data-selected={i === clampedIndex ? "true" : "false"}
						onMouseMove={() => setSelectedIndex(i)}
						onClick={() => handleOpenItem(item)}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							padding: "6px 8px",
							borderRadius: "var(--radius-2)",
							cursor: "pointer",
							background: i === clampedIndex ? "var(--menu-hover-bg)" : "transparent",
							fontSize: "var(--text-caption)",
						}}
					>
						<FileIcon type={getFileIconType(item.name)} />
						<span style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "baseline", gap: 6 }}>
							<span
								style={{
									color: i === clampedIndex ? "var(--fg-0)" : "var(--fg-1)",
									whiteSpace: "nowrap",
								}}
							>
								<HighlightedText runs={item.nameRuns} />
							</span>
							{item.dir && (
								<span
									style={{
										minWidth: 0,
										flex: 1,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										color: "var(--fg-3)",
										fontSize: "var(--text-caption)",
									}}
								>
									<HighlightedText runs={item.dirRuns} />
								</span>
							)}
						</span>
					</li>
				))}
			</ul>
			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					gap: 12,
					padding: "6px 12px",
					boxShadow: "0 -1px 0 var(--line-1)",
					fontSize: "var(--text-section)",
					color: "var(--fg-3)",
					fontFamily: "var(--font-mono)",
				}}
			>
				<span>↑↓ 移動</span>
				<span>⏎ 開く</span>
				<span>esc 閉じる</span>
			</div>
		</dialog>
	);
}
